/*
 * Copyright (c) 2017 Datarista, Inc.
 */

/* eslint-disable consistent-return */

import { polyfill } from 'es6-promise';
import { push } from 'react-router-redux';
import store from 'store';
import { startAction, completeAction, handleError, reportError, reportSuccess } from './common';
import { storeWithExpiration } from '../utils/common';
import { APP_TOKEN_EXPIRATION } from '../config/app_config';
import { accountServiceClient } from '../utils/api';

polyfill();

export const actionTypes = {
  AUTH_LOGIN_REQUEST: 'AUTH_LOGIN_REQUEST',
  AUTH_LOGIN_SUCCESS: 'AUTH_LOGIN_SUCCESS',
  AUTH_LOGIN_FAILURE: 'AUTH_LOGIN_FAILURE',
  AUTH_LOGIN_ERROR: 'AUTH_LOGIN_ERROR',
  AUTH_LOGOUT_REQUEST: 'AUTH_LOGOUT_REQUEST',
  AUTH_LOGOUT_SUCCESS: 'AUTH_LOGOUT_SUCCESS',
  AUTH_REFRESH_REQUEST: 'AUTH_REFRESH_REQUEST',
  AUTH_REFRESH_SUCCESS: 'AUTH_REFRESH_SUCCESS',
  AUTH_REFRESH_ERROR: 'AUTH_REFRESH_ERROR',
  AUTH_CHECK_REQUEST: 'AUTH_TOKEN_CHECK_REQUEST',
  AUTH_CHECK_SUCCESS: 'AUTH_TOKEN_CHECK_SUCCESS',
  AUTH_CHECK_ERROR: 'AUTH_TOKEN_CHECK_ERROR',
  AUTH_PASSWORD_RESET_REQUEST: 'AUTH_PASSWORD_RESET_REQUEST',
  AUTH_PASSWORD_RESET_SUCCESS: 'AUTH_PASSWORD_RESET_SUCCESS',
  AUTH_PASSWORD_RESET_ERROR: 'AUTH_PASSWORD_RESET_ERROR',
  FORGOT_PASSWORD_SENT: 'FORGOT_PASSWORD_SENT',
};

// Connect to the API
const swaggerApi = accountServiceClient();

// Action - Request Login
const requestLogin = () => ({
  type: actionTypes.AUTH_LOGIN_REQUEST,
  isFetching: true,
  isAuthenticated: false,
});

// Action - Receive Login
const receiveLogin = data => ({
  type: actionTypes.AUTH_LOGIN_SUCCESS,
  token: data.token,
  account: data.account,
  accountSettings: data.accountSettings,
  user: data.user,
  isFetching: false,
  isAuthenticated: true,
});

// Log User In
export function logUserIn(credentials, nextLocation) {
  const actionText = 'Logging User In';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(requestLogin());
    return swaggerApi.then(client => {
      client.auth
        .login({
          body: {
            email: credentials.email,
            password: credentials.password,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const authinfo = response.obj;
            // save the token to local storage
            storeWithExpiration.set('token', authinfo.token);
            storeWithExpiration.set('tokenExpiration', new Date().getTime() + APP_TOKEN_EXPIRATION);
            dispatch(receiveLogin(authinfo));
            completeAction(dispatch, actionText);
            // send the user on to the desired destination url
            if (nextLocation) {
              dispatch(push(nextLocation));
            }
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          store.remove('token');
          store.remove('tokenExpiration');
          const statusText = JSON.parse(err.statusText);
          let { message } = statusText;
          if (err.status === 401) {
            message = 'We could not log you in with these credentials';
          }
          const error = new Error(message);
          reportError(dispatch, error);
        });
    });
  };
}

// Action - Request Logout
const requestLogout = () => ({
  type: actionTypes.AUTH_LOGOUT_REQUEST,
  isFetching: true,
  isAuthenticated: true,
});

// Action - Receive Logout
const receiveLogout = () => ({
  type: actionTypes.AUTH_LOGOUT_SUCCESS,
  isFetching: false,
  isAuthenticated: false,
  token: null,
});

// Log User Out
export function logUserOut(nextLocation) {
  const actionText = 'Logging User Out';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(requestLogout());
    // nuke the token from local storage
    store.remove('token');
    store.remove('tokenExpiration');
    dispatch(receiveLogout());
    dispatch(push('/home'));
    completeAction(dispatch, actionText);
    // send the user on to the desired destination url
    if (nextLocation) {
      dispatch(push(nextLocation));
    }
  };
}

// Redirect Logged out user to login page
// Forced to login page (gets set in Template.jsx)
export function redirectToLogin() {
  const actionText = 'Redirecting User to Login';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(requestLogout());
    dispatch(receiveLogout());
    completeAction(dispatch, actionText);
  };
}

// Action - Request Token Refresh
const requestRefresh = (token, isAuthenticated) => ({
  type: actionTypes.AUTH_REFRESH_REQUEST,
  isFetching: true,
  isAuthenticated,
  token,
});

// Action - Receive Token Refresh
const receiveRefresh = data => ({
  type: actionTypes.AUTH_REFRESH_SUCCESS,
  isFetching: false,
  isAuthenticated: true,
  token: data.token,
  user: data.user,
  account: data.account,
  accountSettings: data.accountSettings,
});

// Refresh Token
export function refreshUserToken() {
  const actionText = 'Refreshing User Token';
  const token = storeWithExpiration.get('token');
  return dispatch => {
    startAction(dispatch, actionText, true);
    dispatch(requestRefresh(token, true));
    return swaggerApi.then(client => {
      client.auth
        .refreshToken({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const tokenInfo = response.obj;
            // save the token to local storage
            storeWithExpiration.set('token', tokenInfo.token);
            storeWithExpiration.set('tokenExpiration', new Date().getTime() + APP_TOKEN_EXPIRATION);
            dispatch(receiveRefresh(tokenInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          store.remove('token');
          store.remove('tokenExpiration');
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Action - Request Token Check
const requestCheck = token => ({
  type: actionTypes.AUTH_CHECK_REQUEST,
  isFetching: true,
  isAuthenticated: false, // assume token's valid until API says otherwise
  token,
});

// Action - Receive Token Check
const receiveCheck = data => ({
  type: actionTypes.AUTH_CHECK_SUCCESS,
  isFetching: false,
  isAuthenticated: true,
  token: data.token,
  user: data.user,
  account: data.account,
  accountSettings: data.accountSettings,
});

// Check Token
export function checkUserToken() {
  const actionText = 'Checking User Token';
  const token = storeWithExpiration.get('token');

  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(requestCheck(token));
    return swaggerApi.then(client => {
      client.auth
        .refreshToken({
          Authorization: `bearer ${token}`,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const tokenInfo = response.obj;
            // Note: we don't reset the token expiration.
            // This is just a check, not a new token.
            dispatch(receiveCheck(tokenInfo));
            completeAction(dispatch, actionText);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          store.remove('token');
          store.remove('tokenExpiration');
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// Action - Request Password Reset
const requestPasswordReset = () => ({
  type: actionTypes.AUTH_PASSWORD_RESET_REQUEST,
  isResettingPassword: true,
});

// Action - Reset Password
const receivePasswordReset = data => ({
  type: actionTypes.AUTH_PASSWORD_RESET_SUCCESS,
  isResettingPassword: false,
  data,
});

// Reset Password
export const resetPassword = (credentials, nextLocation) => {
  const actionText = 'Resetting User Password';
  return dispatch => {
    startAction(dispatch, actionText);
    dispatch(requestPasswordReset());
    return swaggerApi.then(client => {
      client.auth
        .resetPassword({
          body: {
            securityToken: credentials.securityToken,
            plainTextPassword: credentials.plainTextPassword,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const userInfo = response.obj;
            // save the token to local storage
            storeWithExpiration.set('token', userInfo.token);
            storeWithExpiration.set('tokenExpiration', new Date().getTime() + APP_TOKEN_EXPIRATION);
            dispatch(receiveLogin(userInfo));
            dispatch(receivePasswordReset(userInfo));
            completeAction(dispatch, actionText);
            // send the user on to the desired destination url
            if (nextLocation) {
              dispatch(push(nextLocation));
            } else dispatch(push('/account/login'));
            reportSuccess(dispatch, 'Your Password Has Been Set.', null);
          } else {
            return Promise.reject(response);
          }
        })
        .catch(err => {
          const statusText = JSON.parse(err.statusText);
          let error = '';
          if (Array.isArray(statusText.errors)) {
            error = new Error(statusText.errors[0]);
          } else {
            error = new Error(statusText.message);
          }
          if (error.message === 'Security token expired') {
            error.message = `${error.message}, please resend a  new reset password email.`;
            dispatch(push('/forgot-password'));
          }
          reportError(dispatch, error);
        });
    });
  };
};

// In case we end up needing to do something with the email address?
const forgotPasswordEmailSent = email => ({
  type: actionTypes.FORGOT_PASSWORD_SENT,
  email,
});

export function sendForgotPasswordEmail(userEmail) {
  const actionText = 'Sending forgot password email';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.auth
        .sendForgotPassword({
          body: {
            email: userEmail,
          },
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            const emailInfo = response.obj;
            dispatch(forgotPasswordEmailSent(emailInfo));
            completeAction(dispatch, actionText);
            dispatch(push('/account/login'));
            reportSuccess(
              dispatch,
              `If this address exists in our system, you will
              receive a password reset email shortly.`
            );
          } else {
            completeAction(dispatch, actionText);
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}

// TODO this is used on the My Account page, and should be removed soon
export function sendResetPasswordEmail(token, userId) {
  const actionText = 'Sending reset password email';
  return dispatch => {
    startAction(dispatch, actionText);
    return swaggerApi.then(client => {
      client.auth
        .sendInvite({
          Authorization: `bearer ${token}`,
          user_id: userId,
        })
        .then(response => {
          // If everything's cool, dispatch actions
          if (response.status >= 200 && response.status < 300) {
            dispatch(requestLogout());
            store.remove('token');
            store.remove('tokenExpiration');
            dispatch(receiveLogout());
            completeAction(dispatch, actionText);
            dispatch(push('/account/login'));
            reportSuccess(
              dispatch,
              `If this address exists in our system, you will
              receive a password reset email shortly.`
            );
          } else {
            completeAction(dispatch, actionText);
            return Promise.reject(response);
          }
        })
        .catch(err => {
          handleError(err, dispatch, actionText);
        });
    });
  };
}
